import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Alert,
  Button,
  Col,
  Container,
  Nav,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import TopNav from '../TopNav/TopNav';
import axiosInstance from '../../axios/axiosConfig';
import PdfViewerModal from '../common/PdfViewerModal';

const TAB_CONFIG = [
  { key: 'guidelines', label: 'Guidelines', folderName: 'Guideline' },
  { key: 'policy', label: 'Policy', folderName: 'Policy' },
  { key: 'specification', label: 'Specification', folderName: 'Specification' },
  { key: 'standard', label: 'Standard', folderName: 'Standard' },
];

const ROOT_FOLDER_NAME = 'Engineering Design Standards';
const DOCUMENTS_FOLDER_NAME = 'Documents';
const normalizeName = (value) => String(value || '').trim().toLowerCase();

function EngineeringDesignDocuments() {
  const { instance, accounts } = useMsal();

  // Engineering docs are sourced from the BMS SharePoint site and drive
  const siteId = process.env.REACT_APP_SHAREPOINT_BMS_SITE_ID;
  const driveId = process.env.REACT_APP_SHAREPOINT_BMS_DRIVE_ID;

  // Local state
  const [accessToken, setAccessToken] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_CONFIG[0].key);
  const [tabFolderIds, setTabFolderIds] = useState({});
  const [tabDocuments, setTabDocuments] = useState({});
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState('');
  const [pdfExternalUrl, setPdfExternalUrl] = useState('');
  const [pdfViewerTitle, setPdfViewerTitle] = useState('');

  const activeTabConfig = useMemo(
    () => TAB_CONFIG.find((tab) => tab.key === activeTab),
    [activeTab]
  );
  const activeTabDocuments = tabDocuments[activeTab] || [];
  const activeTabFolderId = tabFolderIds[activeTab];

  const fetchFolderChildren = useCallback(
    async (folderId) => {
      const graphUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${folderId}/children?$top=200`;

      const response = await axiosInstance.get(graphUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data.value || [];
    },
    [siteId, driveId, accessToken]
  );

  const fetchTabDocuments = useCallback(
    async (tabKey, folderId) => {
      if (!folderId) {
        return;
      }

      setLoadingDocuments(true);
      setError(null);

      try {
        const items = await fetchFolderChildren(folderId);

        // Only show files in each tab. Hide nested folders.
        const files = items
          .filter((item) => !item.folder)
          .sort((a, b) => a.name.localeCompare(b.name));

        setTabDocuments((prev) => ({
          ...prev,
          [tabKey]: files,
        }));
      } catch (err) {
        console.error(`Failed to fetch documents for tab ${tabKey}:`, err);
        setError('Failed to fetch tab documents. Please refresh and try again.');
      } finally {
        setLoadingDocuments(false);
      }
    },
    [fetchFolderChildren]
  );

  const initializeDocumentStructure = useCallback(async () => {
    setLoadingStructure(true);
    setError(null);

    try {
      const rootItems = await fetchFolderChildren('root');

      const designStandardsFolder = rootItems.find(
        (item) => item.folder && normalizeName(item.name) === normalizeName(ROOT_FOLDER_NAME)
      );

      if (!designStandardsFolder) {
        throw new Error(`Could not find "${ROOT_FOLDER_NAME}" in the SharePoint root folder.`);
      }

      const designStandardsChildren = await fetchFolderChildren(designStandardsFolder.id);

      const documentsFolder = designStandardsChildren.find(
        (item) => item.folder && normalizeName(item.name) === normalizeName(DOCUMENTS_FOLDER_NAME)
      );

      if (!documentsFolder) {
        throw new Error(
          `Could not find "${DOCUMENTS_FOLDER_NAME}" under "${ROOT_FOLDER_NAME}".`
        );
      }

      const documentsChildren = await fetchFolderChildren(documentsFolder.id);
      const nextTabFolderIds = {};

      TAB_CONFIG.forEach((tab) => {
        const match = documentsChildren.find(
          (item) => item.folder && normalizeName(item.name) === normalizeName(tab.folderName)
        );

        if (match) {
          nextTabFolderIds[tab.key] = match.id;
        }
      });

      setTabFolderIds(nextTabFolderIds);

      const discoveredTabCount = Object.keys(nextTabFolderIds).length;
      if (discoveredTabCount === 0) {
        throw new Error(
          'None of the required folders were found under /Engineering Design Standards/Documents.'
        );
      }

      const defaultTabKey = TAB_CONFIG[0].key;

      // Do not force tab switching here. This avoids redirect-like behavior
      // when users select a tab and background initialization completes.
      if (!nextTabFolderIds[defaultTabKey]) {
        return;
      }

      await fetchTabDocuments(defaultTabKey, nextTabFolderIds[defaultTabKey]);
    } catch (err) {
      console.error('Failed to initialize Engineering Design Documents structure:', err);
      setError(err.message || 'Failed to initialize Engineering Design Documents.');
    } finally {
      setLoadingStructure(false);
    }
  }, [fetchFolderChildren, fetchTabDocuments]);

  // Get access token on component mount
  useEffect(() => {
    const getAccessToken = async () => {
      try {
        if (!instance || !accounts || accounts.length === 0) {
          setTokenError('No MSAL instance or accounts available');
          return;
        }

        const tokenRequest = {
          scopes: ['https://graph.microsoft.com/.default'],
          account: accounts[0],
        };

        const response = await instance.acquireTokenSilent(tokenRequest);
        setAccessToken(response.accessToken);
        setTokenError(null);
      } catch (error) {
        console.error('Failed to acquire token:', error);
        setTokenError('Failed to acquire access token. Please sign in.');
      }
    };

    if (instance && accounts.length > 0) {
      getAccessToken();
    }
  }, [instance, accounts]);

  // Find /Engineering Design Standards/Documents and initialize tab folders
  useEffect(() => {
    if (siteId && driveId && accessToken && !tokenError) {
      initializeDocumentStructure();
    }
  }, [accessToken, siteId, driveId, tokenError, initializeDocumentStructure]);

  const handleTabSelect = async (tabKey) => {
    if (!tabKey) {
      return;
    }

    setError(null);
    setActiveTab(tabKey);
    const folderId = tabFolderIds[tabKey];
    const tabAlreadyLoaded = Object.prototype.hasOwnProperty.call(tabDocuments, tabKey);

    if (folderId && !tabAlreadyLoaded) {
      await fetchTabDocuments(tabKey, folderId);
    }
  };

  const handleRefreshActiveTab = async () => {
    if (!activeTabFolderId) {
      return;
    }

    await fetchTabDocuments(activeTab, activeTabFolderId);
  };

  const handleViewClick = async (item) => {
    const isPdf = isViewableFile(item?.name);
    if (!isPdf) {
      return;
    }

    if (!item?.id || !accessToken) {
      setError('Unable to open PDF preview. Missing file id or access token.');
      return;
    }

    setError(null);
    setLoadingPdf(true);
    setPdfViewerTitle(item.name || 'PDF Document');
    setPdfExternalUrl(item.webUrl || item['@microsoft.graph.downloadUrl'] || '');
    setPdfViewerUrl((prev) => {
      if (prev && prev.startsWith('blob:')) {
        URL.revokeObjectURL(prev);
      }
      return '';
    });
    setShowPdfModal(true);

    try {
      const graphContentUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${item.id}/content`;

      const response = await axiosInstance.get(graphContentUrl, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const objectUrl = URL.createObjectURL(response.data);
      setPdfViewerUrl(objectUrl);
    } catch (err) {
      console.error('Failed to load PDF preview:', err);
      setPdfViewerUrl('');
      setError('Failed to load PDF preview. Use Open in New Tab as a fallback.');
    } finally {
      setLoadingPdf(false);
    }
  };

  const isViewableFile = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.split('.').pop().toLowerCase();
    return ['pdf'].includes(extension);
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownloadClick = (item) => {
    if (item['@microsoft.graph.downloadUrl']) {
      window.open(item['@microsoft.graph.downloadUrl'], '_blank');
    }
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    setPdfViewerUrl((prev) => {
      if (prev && prev.startsWith('blob:')) {
        URL.revokeObjectURL(prev);
      }
      return '';
    });
    setPdfExternalUrl('');
    setPdfViewerTitle('');
    setLoadingPdf(false);
  };

  useEffect(() => {
    return () => {
      if (pdfViewerUrl && pdfViewerUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfViewerUrl);
      }
    };
  }, [pdfViewerUrl]);

  if (tokenError) {
    return (
      <div>
        <TopNav />
        <Container className="mt-4">
          <Alert variant="danger">{tokenError}</Alert>
        </Container>
      </div>
    );
  }

  if (!siteId || !driveId) {
    return (
      <div>
        <TopNav />
        <Container className="mt-4">
          <Alert variant="warning">
            Engineering SharePoint configuration is missing. Please check REACT_APP_SHAREPOINT_BMS_SITE_ID and REACT_APP_SHAREPOINT_BMS_DRIVE_ID.
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <TopNav />
      <div style={{ padding: '20px' }}>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <h3 className="mb-1">Engineering Design Documents</h3>
            <div className="text-muted" style={{ fontSize: '0.9rem' }}>
              /Engineering Design Standards/Documents/{activeTabConfig?.folderName || ''}
            </div>
          </div>
          <div>
            <Button
              variant="outline-primary"
              onClick={handleRefreshActiveTab}
              disabled={loadingDocuments || loadingStructure || !activeTabFolderId}
            >
              Refresh
            </Button>
          </div>
        </div>

        <Nav
          variant="tabs"
          activeKey={activeTab}
          onSelect={handleTabSelect}
          className="mb-3"
        >
          {TAB_CONFIG.map((tab) => (
            <Nav.Item key={tab.key}>
              <Nav.Link eventKey={tab.key} disabled={loadingStructure}>
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" dismissible>
                <strong>Error:</strong> {error}
              </Alert>
            </Col>
          </Row>
        )}

        {(loadingStructure || loadingDocuments) && (
          <Row className="mb-3">
            <Col className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </Col>
          </Row>
        )}

        {!loadingStructure && !activeTabFolderId && !error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="warning">
                The "{activeTabConfig?.folderName}" folder was not found under
                {' '}/Engineering Design Standards/Documents.
              </Alert>
            </Col>
          </Row>
        )}

        {!loadingStructure && !loadingDocuments && activeTabFolderId && activeTabDocuments.length > 0 && (
          <Table striped bordered hover responsive>
            <thead className="table-light">
              <tr>
                <th style={{ width: '50%' }}>Name</th>
                <th style={{ width: '20%' }}>Modified</th>
                <th style={{ width: '10%' }}>Size</th>
                <th style={{ width: '20%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeTabDocuments.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>
                    {item.lastModifiedDateTime
                      ? new Date(item.lastModifiedDateTime).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>{formatFileSize(item.size)}</td>
                  <td>
                    {item['@microsoft.graph.downloadUrl'] && (
                      <div className="d-flex gap-2">
                        {isViewableFile(item.name) && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleViewClick(item)}
                          >
                            View
                          </Button>
                        )}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleDownloadClick(item)}
                        >
                          Download
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {!loadingStructure && !loadingDocuments && activeTabFolderId && activeTabDocuments.length === 0 && !error && (
          <Alert variant="info">No files found in this tab.</Alert>
        )}

        <PdfViewerModal
          show={showPdfModal}
          onHide={handleClosePdfModal}
          title={pdfViewerTitle}
          loading={loadingPdf}
          viewerUrl={pdfViewerUrl}
          externalUrl={pdfExternalUrl}
          dialogClassName="engineering-pdf-viewer-modal"
        />
      </div>
    </div>
  );
}

export default EngineeringDesignDocuments;
